import React from 'react';
import './ErrorBoundary.css';
import logCollector from '../utils/logger';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Application Error:', error);
    logCollector.addReactError(error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => window.location.reload();

  handleCopyError = () => {
    const { error, errorInfo } = this.state;
    const errorText = [
      'Error Report',
      '============',
      `Error: ${error?.name || 'Unknown'} - ${error?.message || 'An unexpected error occurred'}`,
      `Stack: ${error?.stack || 'N/A'}`,
      `Component: ${errorInfo?.componentStack || 'N/A'}`,
      `Time: ${new Date().toISOString()}`,
      `URL: ${window.location.href}`
    ].join('\n');

    navigator.clipboard.writeText(errorText).catch(() => {
      alert('Failed to copy. Please select and copy manually.');
    });
  };

  handleDownloadLogs = (format) => {
    try {
      format === 'json' ? logCollector.exportLogsAsJSON() : logCollector.exportLogsAsText();
    } catch (err) {
      console.error('Failed to download logs:', err);
    }
  };

  render() {
    const isDev = process.env.NODE_ENV === 'development';

    // Do not render children after an error: React would re-enter the failing subtree and
    // can end up with a blank page instead of this fallback UI.
    if (this.state.hasError) {
      return (
        <div className="error-overlay">
          <div className="error-card">
            <div className="error-card-accent" />
            <div className="error-icon">
              <svg width="80" height="80" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.3" />
                <path d="M50 15 L85 75 L15 75 Z" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                <line x1="50" y1="35" x2="50" y2="55" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
                <circle cx="50" cy="65" r="3" fill="currentColor" />
              </svg>
            </div>
            <h1 className="error-title">Oops! Something went wrong</h1>
            <p className="error-message">
              The page encountered an unexpected error. Please try refreshing.
            </p>
            <button className="error-reload-btn" onClick={this.handleReload}>
              Refresh Page
            </button>
            {isDev && (
              <details className="error-dev-details">
                <summary className="error-dev-summary">Developer Details</summary>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                  <button className="error-dev-btn" onClick={this.handleCopyError}>
                    Copy Error Details
                  </button>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="error-dev-btn error-dev-btn-small" onClick={() => this.handleDownloadLogs('json')}>
                      Download JSON
                    </button>
                    <button className="error-dev-btn error-dev-btn-small" onClick={() => this.handleDownloadLogs('text')}>
                      Download TXT
                    </button>
                  </div>
                </div>
                <div className="error-stack-container">
                  <p className="error-name">{this.state.error?.name || 'Error'}</p>
                  <p className="error-detail-msg">{this.state.error?.message || 'An unexpected error occurred'}</p>
                  <pre className="error-stack">{this.state.error?.stack || String(this.state.error)}</pre>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
